import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { API_BASE_URL, authFetch } from '../../config/api';
import PageHeader from '../../components/PageHeader';

const baseUrl = API_BASE_URL;

/* ── Types (shared contract with backend /api/social) ─────────── */

interface CriticViolation {
  excerpt?: string;
  reason?: string;
}

interface CriticCheckResult {
  pass?: boolean;
  violations?: CriticViolation[];
}

interface CriticVerdict {
  pass?: boolean;
  checks?: Record<string, CriticCheckResult>;
  suggested_fix?: string;
  [key: string]: unknown;
}

interface SocialPost {
  id: number;
  week_start: string;
  post_type: string;
  draft_text: string;
  context_json: unknown;
  critic_verdict: CriticVerdict | null;
  critic_passed: boolean | null;
  status: string;
  post_urn: string | null;
  permalink: string | null;
  utm_campaign: string | null;
  stats: Record<string, unknown>;
  error: string | null;
  reviewer_notes: string | null;
  created_at: string;
  reviewed_at: string | null;
  posted_at: string | null;
}

interface FeedbackItem {
  id: number;
  source: string;
  external_id: string | null;
  social_post_id: number | null;
  received_at: string;
  persona: string | null;
  author_display: string | null;
  contact_email: string | null;
  body: string;
  category: string | null;
  action: string | null;
  created_at: string;
}

type FeedbackPatch = { category?: string | null; action?: string | null; persona?: string | null };

const POST_STATUSES = ['drafted', 'sample', 'emailed', 'held', 'approved', 'posted', 'failed', 'rejected'];
const FEEDBACK_SOURCES = ['website', 'linkedin_comment', 'linkedin_dm', 'email', 'manual'];
const FEEDBACK_CATEGORIES = ['feature_request', 'data_gap', 'ux_issue', 'positive', 'other'];
const FEEDBACK_ACTIONS = ['roadmap', 'replied', 'none'];
const PERSONAS = ['quality_manager', 'consultant', 'other'];

/* ── API calls ────────────────────────────────────────────────── */

async function fetchPosts(): Promise<SocialPost[]> {
  const res = await authFetch(`${baseUrl}/api/social/posts`, { cache: 'no-store' });
  if (!res.ok) throw new Error(await res.text());
  const data = await res.json() as { ok: boolean; posts: SocialPost[] };
  if (!data.ok) throw new Error('Server responded ok: false');
  return data.posts;
}

async function approvePost(params: { id: number; notes: string }): Promise<void> {
  const res = await authFetch(`${baseUrl}/api/social/posts/${params.id}/approve`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params.notes ? { reviewer_notes: params.notes } : {}),
  });
  if (!res.ok) throw new Error(await res.text());
}

async function rejectPost(params: { id: number; notes: string }): Promise<void> {
  const res = await authFetch(`${baseUrl}/api/social/posts/${params.id}/reject`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params.notes ? { reviewer_notes: params.notes } : {}),
  });
  if (!res.ok) throw new Error(await res.text());
}

async function editPost(params: { id: number; draft_text: string }): Promise<void> {
  const res = await authFetch(`${baseUrl}/api/social/posts/${params.id}/edit`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ draft_text: params.draft_text }),
  });
  if (!res.ok) throw new Error(await res.text());
}

async function retryPost(id: number): Promise<void> {
  const res = await authFetch(`${baseUrl}/api/social/posts/${id}/retry`, { method: 'POST' });
  if (!res.ok) throw new Error(await res.text());
}

async function fetchFeedback(source: string, category: string, action: string): Promise<FeedbackItem[]> {
  const params = new URLSearchParams();
  if (source) params.set('source', source);
  if (category) params.set('category', category);
  if (action) params.set('action', action);
  const qs = params.toString();
  const res = await authFetch(`${baseUrl}/api/social/feedback${qs ? `?${qs}` : ''}`, { cache: 'no-store' });
  if (!res.ok) throw new Error(await res.text());
  const data = await res.json() as { ok: boolean; feedback: FeedbackItem[] };
  if (!data.ok) throw new Error('Server responded ok: false');
  return data.feedback;
}

async function updateFeedback(params: { id: number; patch: FeedbackPatch }): Promise<void> {
  const res = await authFetch(`${baseUrl}/api/social/feedback/${params.id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params.patch),
  });
  if (!res.ok) throw new Error(await res.text());
}

/* ── Helpers ──────────────────────────────────────────────────── */

function statusBadgeClass(status: string): string {
  switch (status) {
    case 'held':
    case 'drafted': return 'staging-badge--pending';
    case 'approved':
    case 'posted': return 'staging-badge--approved';
    case 'failed':
    case 'rejected': return 'staging-badge--rejected';
    case 'emailed':
    case 'sample': return 'staging-badge--edited';
    default: return '';
  }
}

function formatDate(dateStr: string): string {
  try {
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: '2-digit', month: 'short', year: 'numeric',
    });
  } catch {
    return dateStr;
  }
}

function labelize(value: string): string {
  return value.replace(/_/g, ' ');
}

/* ── Critic verdict rendering ─────────────────────────────────── */

function CriticVerdictView({ verdict }: { verdict: CriticVerdict | null }) {
  if (!verdict) return <p className="admin-muted">No critic verdict recorded.</p>;

  const rawChecks = verdict.checks;
  let checks: { label: string; passed: boolean; violations: CriticViolation[] }[] = [];
  if (rawChecks && typeof rawChecks === 'object' && !Array.isArray(rawChecks)) {
    checks = Object.entries(rawChecks).map(([name, c]) => ({
      label: name,
      passed: c?.pass === true,
      violations: Array.isArray(c?.violations) ? c.violations : [],
    }));
  }
  const suggestedFix = typeof verdict.suggested_fix === 'string' ? verdict.suggested_fix.trim() : '';

  if (checks.length === 0 && !suggestedFix) {
    // Unknown shape — fall back to raw JSON so nothing is silently hidden.
    return (
      <pre style={{ whiteSpace: 'pre-wrap', fontSize: '0.78rem', background: 'var(--color-bg-subtle)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-sm)', padding: 'var(--space-sm)', margin: 0 }}>
        {JSON.stringify(verdict, null, 2)}
      </pre>
    );
  }

  return (
    <div>
      {checks.length > 0 && (
        <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
          {checks.map((c, i) => (
            <li key={i} style={{ fontSize: '0.85rem' }}>
              <span style={{ color: c.passed ? 'var(--color-primary)' : 'var(--color-error)', fontWeight: 700, marginRight: 6 }}>
                {c.passed ? '✓' : '✗'}
              </span>
              {labelize(c.label)}
              {!c.passed && c.violations.length > 0 && (
                <ul style={{ margin: '4px 0 0', paddingLeft: 22, display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {c.violations.map((v, j) => (
                    <li key={j} style={{ fontSize: '0.82rem', color: 'var(--color-error)' }}>
                      {v.excerpt ? `"${v.excerpt}"` : ''}
                      {v.excerpt && v.reason ? ' — ' : ''}
                      {v.reason ?? ''}
                    </li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ul>
      )}
      {suggestedFix && (
        <p style={{ fontSize: '0.85rem', margin: checks.length > 0 ? 'var(--space-sm) 0 0' : 0 }}>
          <span className={'staging-badge staging-badge--pending'} style={{ marginRight: 6 }}>Suggested fix</span>
          {suggestedFix}
        </p>
      )}
    </div>
  );
}

/* ── Expanded post detail ─────────────────────────────────────── */

interface PostDetailProps {
  post: SocialPost;
  onApprove: (notes: string) => void;
  onReject: (notes: string) => void;
  onSaveEdit: (draftText: string) => void;
  onRetry: () => void;
  busy: boolean;
  mutError: Error | null;
}

function PostDetail({ post, onApprove, onReject, onSaveEdit, onRetry, busy, mutError }: PostDetailProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(post.draft_text);
  const [notes, setNotes] = useState('');

  const detailLabel: React.CSSProperties = {
    fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase',
    letterSpacing: '0.05em', color: 'var(--color-text-secondary)',
    margin: '0 0 6px',
  };

  return (
    <div style={{ padding: 'var(--space-md)', background: 'var(--color-bg-subtle)', display: 'flex', flexDirection: 'column', gap: 'var(--space-md)' }}>
      <div>
        <p style={detailLabel}>Draft text</p>
        {editing ? (
          <textarea
            value={draft}
            onChange={e => setDraft(e.target.value)}
            rows={10}
            className="form-input"
            style={{ width: '100%', resize: 'vertical', lineHeight: 1.5 }}
          />
        ) : (
          <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'var(--font-sans)', fontSize: '0.88rem', lineHeight: 1.55, background: 'var(--color-bg)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-sm)', padding: 'var(--space-md)', margin: 0 }}>
            {post.draft_text}
          </pre>
        )}
      </div>

      <div>
        <p style={detailLabel}>Critic verdict</p>
        <CriticVerdictView verdict={post.critic_verdict} />
      </div>

      {post.error && (
        <div>
          <p style={detailLabel}>Error</p>
          <p className="admin-error" style={{ margin: 0 }}>{post.error}</p>
        </div>
      )}

      {post.reviewer_notes && (
        <div>
          <p style={detailLabel}>Reviewer notes</p>
          <p style={{ fontSize: '0.85rem', margin: 0 }}>{post.reviewer_notes}</p>
        </div>
      )}

      {/* Actions */}
      {editing ? (
        <div style={{ display: 'flex', gap: 'var(--space-sm)' }}>
          <button
            className="btn btn--primary btn--sm"
            disabled={busy || !draft.trim()}
            onClick={() => { onSaveEdit(draft); setEditing(false); }}
          >
            Save draft
          </button>
          <button
            className="btn btn--secondary btn--sm"
            disabled={busy}
            onClick={() => { setDraft(post.draft_text); setEditing(false); }}
          >
            Cancel
          </button>
        </div>
      ) : (
        <>
          {(post.status === 'held' || post.status === 'failed' || post.status === 'emailed') && (
            <div style={{ display: 'flex', gap: 'var(--space-sm)', alignItems: 'center', flexWrap: 'wrap' }}>
              <input
                type="text"
                value={notes}
                onChange={e => setNotes(e.target.value)}
                placeholder="Reviewer notes (optional)"
                className="form-input"
                style={{ flex: 1, minWidth: 220 }}
              />
              {post.status === 'held' && (
                <>
                  <button className="btn btn--secondary btn--sm" disabled={busy} onClick={() => setEditing(true)}>
                    Edit
                  </button>
                  <button
                    className="btn btn--primary btn--sm"
                    disabled={busy}
                    onClick={() => {
                      if (window.confirm('This will publish to LinkedIn. Continue?')) onApprove(notes);
                    }}
                  >
                    Approve
                  </button>
                </>
              )}
              {post.status === 'failed' && (
                <button className="btn btn--primary btn--sm" disabled={busy} onClick={onRetry}>
                  Retry
                </button>
              )}
              <button className="btn btn--danger-ghost btn--sm" disabled={busy} onClick={() => onReject(notes)}>
                Reject
              </button>
            </div>
          )}
        </>
      )}

      {mutError && <p className="admin-error" style={{ margin: 0 }}>{mutError.message}</p>}
    </div>
  );
}

/* ── Posts tab ────────────────────────────────────────────────── */

function PostsTab() {
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState('');
  const [expandedId, setExpandedId] = useState<number | null>(null);

  const { data: posts, isLoading, isError, error } = useQuery<SocialPost[], Error>(
    ['socialPosts'],
    fetchPosts,
  );

  const invalidate = () => queryClient.invalidateQueries(['socialPosts']);
  const approveMut = useMutation<void, Error, { id: number; notes: string }>(approvePost, { onSuccess: invalidate });
  const rejectMut = useMutation<void, Error, { id: number; notes: string }>(rejectPost, { onSuccess: invalidate });
  const editMut = useMutation<void, Error, { id: number; draft_text: string }>(editPost, { onSuccess: invalidate });
  const retryMut = useMutation<void, Error, number>(retryPost, { onSuccess: invalidate });

  const busy = approveMut.isLoading || rejectMut.isLoading || editMut.isLoading || retryMut.isLoading;
  const mutError = approveMut.error ?? rejectMut.error ?? editMut.error ?? retryMut.error;

  const filtered = posts?.filter(p => !statusFilter || p.status === statusFilter) ?? [];

  return (
    <section className="card admin-section">
      <div className="card-header">
        <h3>Posts</h3>
        <select
          value={statusFilter}
          onChange={e => { setStatusFilter(e.target.value); setExpandedId(null); }}
          className="form-select"
          aria-label="Filter posts by status"
        >
          <option value="">All statuses</option>
          {POST_STATUSES.map(s => (
            <option key={s} value={s}>{labelize(s)}</option>
          ))}
        </select>
      </div>
      <div className="card-body admin-section__body admin-section__body--flush">
        {isLoading && <p className="admin-muted admin-section__padded">Loading posts…</p>}
        {isError && <p className="admin-error admin-section__padded">Error: {error.message}</p>}

        {!isLoading && !isError && filtered.length === 0 && (
          <p className="admin-empty">No posts{statusFilter ? ` with status "${labelize(statusFilter)}"` : ' yet'}.</p>
        )}

        {filtered.length > 0 && (
          <div className="data-table__wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Week</th>
                  <th>Type</th>
                  <th>Status</th>
                  <th>Critic</th>
                  <th>Link</th>
                  <th>Created</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(p => (
                  <React.Fragment key={p.id}>
                    <tr
                      onClick={() => setExpandedId(expandedId === p.id ? null : p.id)}
                      style={{ cursor: 'pointer' }}
                    >
                      <td className="admin-cell--nowrap admin-cell--primary">{formatDate(p.week_start)}</td>
                      <td className="admin-cell--nowrap">{labelize(p.post_type)}</td>
                      <td>
                        <span className={`staging-badge ${statusBadgeClass(p.status)}`}>{p.status}</span>
                      </td>
                      <td className="admin-cell--nowrap">
                        {p.critic_passed === true && (
                          <span style={{ color: 'var(--color-primary)', fontWeight: 600 }}>{'✓'} pass</span>
                        )}
                        {p.critic_passed === false && (
                          <span style={{ color: 'var(--color-error)', fontWeight: 600 }}>{'✗'} fail</span>
                        )}
                        {p.critic_passed === null && <span className="admin-muted">—</span>}
                      </td>
                      <td>
                        {p.status === 'posted' && p.permalink ? (
                          <a
                            href={p.permalink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="admin-link"
                            onClick={e => e.stopPropagation()}
                          >
                            View post
                          </a>
                        ) : (
                          <span className="admin-muted">—</span>
                        )}
                      </td>
                      <td className="admin-muted admin-cell--nowrap">{formatDate(p.created_at)}</td>
                    </tr>
                    {expandedId === p.id && (
                      <tr>
                        <td colSpan={6} style={{ padding: 0 }}>
                          <PostDetail
                            key={p.id}
                            post={p}
                            onApprove={notes => approveMut.mutate({ id: p.id, notes })}
                            onReject={notes => rejectMut.mutate({ id: p.id, notes })}
                            onSaveEdit={draftText => editMut.mutate({ id: p.id, draft_text: draftText })}
                            onRetry={() => retryMut.mutate(p.id)}
                            busy={busy}
                            mutError={mutError}
                          />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
}

/* ── Feedback tab ─────────────────────────────────────────────── */

function FeedbackTab() {
  const queryClient = useQueryClient();
  const [sourceFilter, setSourceFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());

  const { data: feedback, isLoading, isError, error } = useQuery<FeedbackItem[], Error>(
    ['socialFeedback', sourceFilter, categoryFilter, actionFilter],
    () => fetchFeedback(sourceFilter, categoryFilter, actionFilter),
    { keepPreviousData: true },
  );

  const updateMut = useMutation<void, Error, { id: number; patch: FeedbackPatch }>(updateFeedback, {
    onSuccess: () => queryClient.invalidateQueries(['socialFeedback']),
  });

  function toggleExpanded(id: number) {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const filterSelect = (
    value: string,
    onChange: (v: string) => void,
    allLabel: string,
    options: string[],
    ariaLabel: string,
  ) => (
    <select value={value} onChange={e => onChange(e.target.value)} className="form-select" aria-label={ariaLabel}>
      <option value="">{allLabel}</option>
      {options.map(o => <option key={o} value={o}>{labelize(o)}</option>)}
    </select>
  );

  const inlineSelect = (
    item: FeedbackItem,
    field: keyof FeedbackPatch,
    options: string[],
  ) => (
    <select
      value={item[field] ?? ''}
      onChange={e => {
        const v = e.target.value || null;
        const patch: FeedbackPatch =
          field === 'persona' ? { persona: v } :
          field === 'category' ? { category: v } :
          { action: v };
        updateMut.mutate({ id: item.id, patch });
      }}
      className="form-select"
      disabled={updateMut.isLoading}
      aria-label={`Set ${field}`}
    >
      <option value="">—</option>
      {options.map(o => <option key={o} value={o}>{labelize(o)}</option>)}
    </select>
  );

  return (
    <section className="card admin-section">
      <div className="card-header">
        <h3>Feedback</h3>
        {feedback && <span className="card-badge">{feedback.length} shown</span>}
      </div>
      <div className="card-body admin-section__body admin-section__body--flush">
        <div className="admin-toolbar">
          {filterSelect(sourceFilter, setSourceFilter, 'All sources', FEEDBACK_SOURCES, 'Filter by source')}
          {filterSelect(categoryFilter, setCategoryFilter, 'All categories', FEEDBACK_CATEGORIES, 'Filter by category')}
          {filterSelect(actionFilter, setActionFilter, 'All actions', FEEDBACK_ACTIONS, 'Filter by action')}
        </div>

        {isLoading && <p className="admin-muted admin-section__padded">Loading feedback…</p>}
        {isError && <p className="admin-error admin-section__padded">Error: {error.message}</p>}
        {updateMut.error && <p className="admin-error admin-section__padded">Update failed: {updateMut.error.message}</p>}

        {!isLoading && !isError && feedback?.length === 0 && (
          <p className="admin-empty">No feedback matches the current filters.</p>
        )}

        {feedback && feedback.length > 0 && (
          <div className="data-table__wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Received</th>
                  <th>Source</th>
                  <th>From</th>
                  <th>Message</th>
                  <th>Persona</th>
                  <th>Category</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {feedback.map(f => {
                  const expanded = expandedIds.has(f.id);
                  const truncated = f.body.length > 160 && !expanded;
                  return (
                    <tr key={f.id}>
                      <td className="admin-muted admin-cell--nowrap">{formatDate(f.received_at)}</td>
                      <td className="admin-cell--nowrap">{labelize(f.source)}</td>
                      <td>
                        <div className="admin-cell--primary">{f.author_display || '—'}</div>
                        {f.contact_email && (
                          <div className="admin-muted admin-cell--secondary">{f.contact_email}</div>
                        )}
                      </td>
                      <td
                        onClick={() => f.body.length > 160 && toggleExpanded(f.id)}
                        style={{
                          maxWidth: 380,
                          whiteSpace: 'pre-wrap',
                          fontSize: '0.85rem',
                          cursor: f.body.length > 160 ? 'pointer' : 'default',
                        }}
                        title={f.body.length > 160 ? 'Click to expand or collapse' : undefined}
                      >
                        {truncated ? `${f.body.slice(0, 160)}…` : f.body}
                      </td>
                      <td>{inlineSelect(f, 'persona', PERSONAS)}</td>
                      <td>{inlineSelect(f, 'category', FEEDBACK_CATEGORIES)}</td>
                      <td>{inlineSelect(f, 'action', FEEDBACK_ACTIONS)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
}

/* ── Page ─────────────────────────────────────────────────────── */

type Tab = 'posts' | 'feedback';

export default function SocialAgentPage() {
  const [tab, setTab] = useState<Tab>('posts');

  return (
    <div className="container admin-page">
      <PageHeader
        title="Social Agent"
        tagline="Review LinkedIn post drafts before they go out, and triage reader feedback."
        back={{ to: '/admin', label: 'Back to Manage' }}
      />

      <div className="knowledge-filters">
        {(['posts', 'feedback'] as Tab[]).map(t => (
          <button
            key={t}
            className={`knowledge-filter-btn ${tab === t ? 'knowledge-filter-btn--active' : ''}`}
            onClick={() => setTab(t)}
          >
            {t.charAt(0).toUpperCase() + t.slice(1)}
          </button>
        ))}
      </div>

      {tab === 'posts' ? <PostsTab /> : <FeedbackTab />}
    </div>
  );
}
